/**
 * Accounts List Page
 *
 * Main accounts management page with DataTable (desktop) and card grid (mobile).
 * Includes filtering, sorting, pagination, and bulk actions.
 */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import {
  Button,
  Card,
  CardContent,
  DataTable,
  Skeleton,
  Checkbox,
  Badge,
} from '@respectly/ui';
import { cn, useIsMobile } from '@respectly/ui';
import {
  Plus,
  Building2,
  Users,
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import type { AccountListItem } from '@respectly/shared-types';
import { useAccountsStore } from '../../stores/useAccountsStore';
import {
  AccountStatusBadge,
  BillingStatusBadge,
  AccountFilters,
  AccountCardGrid,
  AccountQuickActions,
} from '../../components/accounts';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut' },
  },
};

// ============================================
// Utility Functions
// ============================================

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ============================================
// Component
// ============================================

export function AccountsList() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const {
    accounts,
    pagination,
    isLoadingList,
    selectedIds,
    sortBy,
    sortOrder,
    loadAccounts,
    refreshAccounts,
    setSorting,
    toggleSelect,
    clearSelection,
  } = useAccountsStore();

  const [plans, setPlans] = useState<{ id: string; name: string }[]>([]);

  // Load accounts and plans on mount
  useEffect(() => {
    loadAccounts(1);
    getApiClient()
      .get<{ id: string; name: string }[]>(API_ENDPOINTS.SUPER_ADMIN.PLANS.LIST)
      .then((res) => setPlans(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, [loadAccounts]);

  // Handle row click
  const handleRowClick = (account: AccountListItem) => {
    navigate({ to: '/accounts/$id', params: { id: account.id } });
  };

  // Handle sort change
  const handleSort = (field: string) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSorting(field as any, newOrder);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    loadAccounts(page);
  };

  // ============================================
  // Table Columns Definition
  // ============================================

  const columns = useMemo(
    () => [
      {
        id: 'select',
        header: '',
        cell: (row: AccountListItem) => (
          <Checkbox
            checked={selectedIds.has(row.id)}
            onCheckedChange={() => toggleSelect(row.id)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        ),
      },
      {
        id: 'name',
        header: 'Account',
        sortable: true,
        cell: (row: AccountListItem) => (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 text-sm font-semibold text-slate-600 dark:from-slate-700 dark:to-slate-800 dark:text-slate-300">
              {getInitials(row.name)}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-slate-900 dark:text-white truncate">
                {row.name}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="truncate">{row.ownerEmail ?? `/${row.slug}`}</span>
                {row.industry && (
                  <>
                    <span className="text-slate-300 dark:text-slate-600">•</span>
                    <span className="truncate">{row.industry}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'plan',
        header: 'Plan',
        cell: (row: AccountListItem) => (
          <Badge variant="outline" className="font-medium">
            {row.plan.name}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        cell: (row: AccountListItem) => <AccountStatusBadge status={row.status} />,
      },
      {
        id: 'billingStatus',
        header: 'Billing',
        cell: (row: AccountListItem) => (
          <div className="flex items-center gap-2">
            <BillingStatusBadge status={row.billingStatus} />
            {(row.openDisputeCount ?? 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400"
                title={`${row.openDisputeCount} open dispute${row.openDisputeCount === 1 ? '' : 's'}`}
              >
                <AlertTriangle className="h-3 w-3" />
                {row.openDisputeCount}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'userCount',
        header: 'Users',
        cell: (row: AccountListItem) => (
          <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
            <Users className="h-4 w-4" />
            <span>{row.userCount}</span>
          </div>
        ),
      },
      {
        id: 'assignedTo',
        header: 'Assigned To',
        cell: (row: AccountListItem) =>
          row.assignedTo ? (
            <span className="text-slate-600 dark:text-slate-400">
              {row.assignedTo.firstName} {row.assignedTo.lastName}
            </span>
          ) : (
            <span className="text-slate-400 dark:text-slate-600">—</span>
          ),
      },
      {
        id: 'createdAt',
        header: 'Created',
        sortable: true,
        cell: (row: AccountListItem) => (
          <span className="text-slate-500">{formatDate(row.createdAt)}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row: AccountListItem) => (
          <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <AccountQuickActions account={row} />
          </div>
        ),
      },
    ],
    [selectedIds, toggleSelect]
  );

  // ============================================
  // Render
  // ============================================

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Page Header */}
      <motion.div variants={itemVariants} className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
            Accounts
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {pagination ? (
              <>
                {pagination.total.toLocaleString()} account{pagination.total !== 1 ? 's' : ''} total
              </>
            ) : (
              'Manage all tenant accounts'
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={refreshAccounts}
            disabled={isLoadingList}
          >
            <RefreshCw className={cn('h-4 w-4', isLoadingList && 'animate-spin')} />
          </Button>

          {/* Export Button */}
          <Button variant="outline" size="sm">
            <Download className="mr-1.5 h-4 w-4" />
            Export
          </Button>

          {/* Create Account Button */}
          <Button onClick={() => navigate({ to: '/accounts/new' })}>
            <Plus className="mr-1.5 h-4 w-4" />
            Create Account
          </Button>
        </div>
      </motion.div>

      {/* Bulk Actions Bar (when items selected) */}
      {selectedIds.size > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800 dark:bg-blue-900/30"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
              {selectedIds.size} account{selectedIds.size !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={clearSelection}>
                Clear Selection
              </Button>
              {/* Add bulk action buttons here as needed */}
            </div>
          </div>
        </motion.div>
      )}

      {/* Filters */}
      <motion.div variants={itemVariants}>
        <AccountFilters plans={plans} />
      </motion.div>

      {/* Content */}
      <motion.div variants={itemVariants}>
        {isLoadingList ? (
          // Loading State
          <LoadingSkeleton isMobile={isMobile} />
        ) : accounts.length === 0 ? (
          // Empty State
          <EmptyState onCreateClick={() => navigate({ to: '/accounts/new' })} />
        ) : isMobile ? (
          // Mobile: Card Grid
          <AccountCardGrid accounts={accounts} />
        ) : (
          // Desktop: DataTable
          <Card>
            <DataTable
              data={accounts}
              columns={columns}
              onRowClick={handleRowClick}
              sortColumn={sortBy}
              sortDirection={sortOrder}
              onSort={(column, direction) => {
                if (direction) {
                  handleSort(column);
                }
              }}
            />
          </Card>
        )}
      </motion.div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <motion.div variants={itemVariants}>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={handlePageChange}
          />
        </motion.div>
      )}
    </motion.div>
  );
}

// ============================================
// Loading Skeleton
// ============================================

function LoadingSkeleton({ isMobile }: { isMobile: boolean }) {
  if (isMobile) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-3">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-9 w-9 rounded-lg" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
              <Skeleton className="h-5 w-20 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Empty State
// ============================================

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="rounded-2xl bg-slate-100 p-4 dark:bg-slate-800">
          <Building2 className="h-10 w-10 text-slate-400" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
          No accounts found
        </h3>
        <p className="mt-1 text-center text-sm text-slate-500">
          Get started by creating your first account,
          <br />
          or try adjusting your filters.
        </p>
        <Button className="mt-6" onClick={onCreateClick}>
          <Plus className="mr-1.5 h-4 w-4" />
          Create Account
        </Button>
      </CardContent>
    </Card>
  );
}

// ============================================
// Pagination Component
// ============================================

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
}

function Pagination({ currentPage, totalPages, total, limit, onPageChange }: PaginationProps) {
  const startItem = (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, total);

  return (
    <div className="flex items-center justify-between rounded-lg border bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-sm text-slate-500">
        Showing <span className="font-medium text-slate-700 dark:text-slate-300">{startItem}</span> to{' '}
        <span className="font-medium text-slate-700 dark:text-slate-300">{endItem}</span> of{' '}
        <span className="font-medium text-slate-700 dark:text-slate-300">{total}</span> results
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1 px-2">
          {generatePageNumbers(currentPage, totalPages).map((page, i) =>
            page === '...' ? (
              <span key={`ellipsis-${i}`} className="px-2 text-slate-400">
                ...
              </span>
            ) : (
              <Button
                key={page}
                variant={currentPage === page ? 'default' : 'ghost'}
                size="sm"
                onClick={() => onPageChange(page as number)}
                className="min-w-[36px]"
              >
                {page}
              </Button>
            )
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function generatePageNumbers(current: number, total: number): (number | string)[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];

  if (current <= 3) {
    pages.push(1, 2, 3, 4, '...', total);
  } else if (current >= total - 2) {
    pages.push(1, '...', total - 3, total - 2, total - 1, total);
  } else {
    pages.push(1, '...', current - 1, current, current + 1, '...', total);
  }

  return pages;
}
